import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { OrgUsage } from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Button,
  Card,
  EmptyState,
  LoadingPane,
  PageHeader,
  Table,
  formatBytes,
  formatDateTime,
} from '../../components/ui.js';

const formatPounds = (p: number) => '£' + (p / 100).toFixed(2);

/** Unit costs can be fractions of a penny — show them as pence when tiny. */
function formatUnitCost(pence: number): string {
  if (pence > 0 && pence < 1) return `${pence.toFixed(3)}p`;
  return formatPounds(pence);
}

function StatTile({
  label,
  value,
  secondary,
}: {
  label: string;
  value: ReactNode;
  secondary?: ReactNode;
}) {
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        padding: 'var(--space-4)',
      }}
    >
      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{label}</div>
      <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, marginTop: 'var(--space-1)' }}>
        {value}
      </div>
      {secondary && (
        <div
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-faint)',
            marginTop: 'var(--space-1)',
          }}
        >
          {secondary}
        </div>
      )}
    </div>
  );
}

const rightAlign = { textAlign: 'right' } as const;

export function UsagePage() {
  const { org } = useOrg();
  const isAdmin = org.role === 'admin';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['usage', org.orgId],
    queryFn: () => apiFetch<OrgUsage>(`/orgs/${org.orgId}/usage`),
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <EmptyState
        title="Admins only"
        message="Usage and cost information can only be viewed by organisation admins."
      />
    );
  }
  if (isError) {
    return (
      <div>
        <PageHeader title="Usage" />
        <EmptyState
          title="Could not load usage"
          message="Something went wrong loading usage and cost information — please try again shortly."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }
  if (isLoading || !data) return <LoadingPane label="Loading usage" />;

  const { counts, cost } = data;

  return (
    <div>
      <PageHeader
        title="Usage"
        subtitle={`Activity across ${org.orgName} and the estimated monthly running cost it generates`}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <StatTile label="Users" value={counts.members.toLocaleString()} />
        <StatTile
          label="File uploads"
          value={counts.evidenceFiles.toLocaleString()}
          secondary={`${formatBytes(counts.evidenceBytes)} stored`}
        />
        <StatTile
          label="Documents created"
          value={counts.corpusItems.toLocaleString()}
          secondary={`${counts.documentVersions.toLocaleString()} versions saved`}
        />
        <StatTile
          label="Imported documents"
          value={counts.importJobs.toLocaleString()}
          secondary={`${formatBytes(counts.importBytes)} stored`}
        />
        <StatTile
          label="Frameworks started"
          value={counts.assessments.toLocaleString()}
          secondary={`${counts.standardsInUse.toLocaleString()} standards in use`}
        />
        <StatTile
          label="Training courses started"
          value={counts.coursesStarted.toLocaleString()}
          secondary={`${counts.coursesCompleted.toLocaleString()} completed`}
        />
        <StatTile label="AI jobs" value={counts.aiJobs.toLocaleString()} />
        <StatTile label="Exports" value={counts.exportsGenerated.toLocaleString()} />
      </div>

      <Card title="Estimated monthly running cost">
        <Table
          head={
            <>
              <th scope="col">Cost line</th>
              <th scope="col" style={rightAlign}>
                Quantity
              </th>
              <th scope="col" style={rightAlign}>
                Unit cost
              </th>
              <th scope="col" style={rightAlign}>
                Line cost
              </th>
            </>
          }
        >
          {cost.lines.map((line) => (
            <tr key={line.key}>
              <td>{line.label}</td>
              <td style={rightAlign}>
                {line.quantity.toLocaleString()} {line.unit}
              </td>
              <td style={rightAlign}>{formatUnitCost(line.unitCostPence)}</td>
              <td style={rightAlign}>{formatPounds(line.costPence)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={3} style={{ ...rightAlign, fontWeight: 600 }}>
              Infrastructure subtotal
            </td>
            <td style={{ ...rightAlign, fontWeight: 600 }}>{formatPounds(cost.subtotalPence)}</td>
          </tr>
        </Table>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-4)',
          }}
        >
          <span style={{ color: 'var(--color-text-muted)' }}>
            Billable estimate: infrastructure cost × {cost.multiplier}
          </span>
          <span style={{ fontSize: 'var(--text-2xl)', fontWeight: 700 }}>
            {formatPounds(cost.totalPence)}
            <span
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 400,
                color: 'var(--color-text-muted)',
                marginLeft: 'var(--space-2)',
              }}
            >
              per month
            </span>
          </span>
        </div>

        <p
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)',
            marginTop: 'var(--space-3)',
          }}
        >
          This is an estimate based on your organisation&rsquo;s infrastructure usage, not an
          invoice — actual charges are set by your plan. Figures generated{' '}
          {formatDateTime(data.generatedAt)}.
        </p>
      </Card>
    </div>
  );
}
